import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const RANGE_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const fmtDay = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const KpiCard = ({ label, value, sub, tone = 'default' }) => (
  <div className={`p-5 rounded-2xl bg-[#141414] border-2 ${tone === 'accent' ? 'border-brand-orange/40' : 'border-white/10'}`}>
    <div className="text-[10px] tracking-[0.32em] uppercase text-white/50 mb-2">{label}</div>
    <div className={`font-display font-black text-3xl sm:text-4xl leading-none ${tone === 'accent' ? 'text-brand-orange' : 'text-white'}`}>
      {value}
    </div>
    {sub && <div className="text-xs text-white/50 mt-2">{sub}</div>}
  </div>
);

const chartAxis = { fill: '#ffffff70', fontSize: 11 };
const tooltipStyle = {
  contentStyle: { background: '#0a0a0a', border: '1px solid #ffffff20', borderRadius: 8, color: '#fff', fontSize: 12 },
  labelStyle: { color: '#FF6600' },
  cursor: { fill: '#ffffff08' },
};

const BarSection = ({ title, data, dataKey = 'count', color = '#FF6600', empty }) => (
  <div className="p-5 rounded-2xl bg-[#141414] border-2 border-white/10">
    <div className="font-display font-black text-lg mb-3">{title}</div>
    {data && data.length > 0 ? (
      <ul className="space-y-2">
        {data.map((item) => (
          <li key={item.name} className="flex items-center gap-3 text-sm">
            <span className="w-32 truncate text-white/80">{item.name}</span>
            <div className="flex-1 h-6 bg-black/50 rounded-lg overflow-hidden">
              <div
                className="h-full rounded-lg transition-all"
                style={{
                  width: `${(item[dataKey] / Math.max(...data.map((d) => d[dataKey]))) * 100}%`,
                  background: color,
                }}
              />
            </div>
            <span className="font-mono text-white/70 text-xs w-10 text-right">{item[dataKey]}</span>
          </li>
        ))}
      </ul>
    ) : (
      <div className="text-white/40 text-sm py-6 text-center">{empty || 'Not enough data yet.'}</div>
    )}
  </div>
);

const AnalyticsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/api/admin/analytics?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError('Could not load analytics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, token]);

  const chartRequests = useMemo(
    () => (data?.requests_per_day || []).map((d) => ({ ...d, day: fmtDay(d.date) })),
    [data],
  );
  const chartOrders = useMemo(
    () => (data?.orders_per_day || []).map((d) => ({ ...d, day: fmtDay(d.date) })),
    [data],
  );

  if (!data && !loading && !error) return null;

  const t = data?.totals || {};
  const pctColor =
    t.requests_pct_change > 0 ? 'text-green-300' :
    t.requests_pct_change < 0 ? 'text-red-300' : 'text-white/50';

  return (
    <div className="space-y-6">
      {/* Range picker */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] tracking-[0.32em] uppercase text-white/50">
          Window
        </div>
        <div className="inline-flex rounded-full bg-[#141414] border-2 border-white/10 p-1">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDays(opt.value)}
              className={`px-4 py-1.5 rounded-full text-xs tracking-[0.2em] uppercase font-display font-black transition-colors ${
                days === opt.value ? 'bg-brand-orange text-black' : 'text-white/70 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="px-5 py-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 text-sm">{error}</div>
      )}
      {loading && (
        <div className="px-5 py-8 rounded-xl bg-[#141414] border border-white/10 text-white/50 text-sm text-center">
          Crunching the numbers…
        </div>
      )}

      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard
              label="Requests · last 7d"
              value={t.requests_last_7d}
              sub={
                <span>
                  <span className={pctColor}>
                    {t.requests_pct_change > 0 ? '↑' : t.requests_pct_change < 0 ? '↓' : '—'}{' '}
                    {Math.abs(t.requests_pct_change)}%
                  </span>{' '}
                  vs prior 7d ({t.requests_prev_7d})
                </span>
              }
              tone="accent"
            />
            <KpiCard
              label="All-time requests"
              value={t.requests}
            />
            <KpiCard
              label="Paid orders"
              value={t.paid_orders}
              sub={`$${(t.revenue || 0).toFixed(2)} total`}
              tone={t.paid_orders > 0 ? 'accent' : 'default'}
            />
            <KpiCard
              label="Request → order rate"
              value={`${t.conversion_rate || 0}%`}
              sub={t.requests > 0 ? `${t.paid_orders}/${t.requests}` : 'awaiting data'}
            />
          </div>

          {/* Trend line: requests per day */}
          <div className="p-5 rounded-2xl bg-[#141414] border-2 border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-black text-lg">Requests · last {days} days</div>
              <div className="text-xs text-white/40 uppercase tracking-[0.22em]">Line</div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRequests} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                  <XAxis dataKey="day" tick={chartAxis} tickLine={false} axisLine={{ stroke: '#ffffff20' }} interval={Math.floor(days / 8)} />
                  <YAxis tick={chartAxis} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Line type="monotone" dataKey="count" stroke="#FF6600" strokeWidth={2.5} dot={{ r: 3, fill: '#FF6600', stroke: 'none' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bar: orders + revenue per day */}
          <div className="p-5 rounded-2xl bg-[#141414] border-2 border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-black text-lg">Orders · last {days} days</div>
              <div className="text-xs text-white/40 uppercase tracking-[0.22em]">Bar</div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartOrders} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                  <XAxis dataKey="day" tick={chartAxis} tickLine={false} axisLine={{ stroke: '#ffffff20' }} interval={Math.floor(days / 8)} />
                  <YAxis tick={chartAxis} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill="#4ade80" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Two-column: top garments + top products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BarSection
              title="Most-requested garments"
              data={data.top_garments}
              empty="No garment data yet."
            />
            <BarSection
              title="Top-selling products"
              data={data.top_products}
              color="#4ade80"
              empty="No paid orders yet. When shirts start selling, they'll show up here."
            />
          </div>

          {/* Two-column: top occasions + status breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BarSection
              title="Occasions people ask for"
              data={data.top_occasions}
              color="#a78bfa"
              empty="No occasion data yet."
            />
            <div className="p-5 rounded-2xl bg-[#141414] border-2 border-white/10">
              <div className="font-display font-black text-lg mb-3">Request status pipeline</div>
              {Object.keys(data.status_breakdown).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.status_breakdown).map(([status, count]) => (
                    <div
                      key={status}
                      className="px-4 py-2.5 rounded-xl bg-black/50 border border-white/10 min-w-[110px]"
                    >
                      <div className="text-[9px] uppercase tracking-[0.24em] text-white/50">{status.replace('_', ' ')}</div>
                      <div className="font-display font-black text-2xl text-white mt-1">{count}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-white/40 text-sm py-6 text-center">No requests yet.</div>
              )}
            </div>
          </div>

          {/* Footnote */}
          <div className="text-xs text-white/40 pt-2 leading-relaxed">
            Data pulled directly from your database. For visitor-side analytics
            (where traffic comes from, page views, geography), check{' '}
            <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer" className="text-brand-orange underline">Google Analytics</a>.
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsTab;
